/**
 * FormFieldPreview (#262) — renderiza um campo do formulário como ele aparece
 * no form público (WYSIWYG), desabilitado (é só preview no canvas do builder).
 */

import React from 'react';

import { FormFieldType, type FormField } from '@troia-v3/types';
import { Icon } from '@/shared/icons';

const inputStyle: React.CSSProperties = {
  backgroundColor: 'var(--theme-input-background)',
  borderColor: 'var(--theme-input-border)',
  color: 'var(--theme-input-text)',
};

export const FormFieldPreview: React.FC<{ field: FormField }> = ({ field }) => {
  const { type, placeholder, options } = field;

  if (type === FormFieldType.HEADING) {
    return <h3 className="text-lg font-bold" style={{ color: 'var(--theme-card-text)' }}>{field.label}</h3>;
  }
  if (type === FormFieldType.PARAGRAPH) {
    return <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>{field.label}</p>;
  }

  const control = ((): React.ReactNode => {
    switch (type) {
      case FormFieldType.LONG_TEXT:
        return <textarea disabled rows={3} placeholder={placeholder} className="w-full text-sm border rounded-lg px-3 py-2 resize-none" style={inputStyle} />;
      case FormFieldType.SELECT:
      case FormFieldType.MULTI_SELECT:
        return (
          <select disabled className="h-[38px] w-full text-sm border rounded-lg px-3 py-2" style={inputStyle}>
            <option>{placeholder || 'Selecione…'}</option>
            {(options || []).map((o) => <option key={o.id}>{o.label}</option>)}
          </select>
        );
      case FormFieldType.RADIO:
      case FormFieldType.CHECKBOX:
        return (
          <div className="space-y-1.5">
            {(options || []).map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-sm" style={{ color: 'var(--theme-card-text)' }}>
                <input type={type === FormFieldType.RADIO ? 'radio' : 'checkbox'} disabled />
                {o.label}
              </label>
            ))}
          </div>
        );
      case FormFieldType.RATING:
        return <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <Icon key={i} name="star" className="w-5 h-5" style={{ color: 'var(--theme-input-border)' }} />)}</div>;
      case FormFieldType.LINEAR_SCALE:
        return <div className="flex gap-2">{[1, 2, 3, 4, 5].map((i) => <span key={i} className="w-8 h-8 rounded-lg border flex items-center justify-center text-sm" style={inputStyle}>{i}</span>)}</div>;
      case FormFieldType.FILE_UPLOAD:
        return (
          <div className="border-2 border-dashed rounded-lg p-4 text-center text-sm" style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-muted-text)' }}>
            <Icon name="upload" className="w-5 h-5 mx-auto mb-1" /> Anexar arquivo
          </div>
        );
      case FormFieldType.DATE:
        return <input type="date" disabled className="h-[38px] w-full text-sm border rounded-lg px-3 py-2" style={inputStyle} />;
      case FormFieldType.TIME:
        return <input type="time" disabled className="h-[38px] w-full text-sm border rounded-lg px-3 py-2" style={inputStyle} />;
      case FormFieldType.PHOTO:
        return (
          <button
            type="button"
            disabled
            className="w-full border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center gap-1.5 text-sm"
            style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="camera" className="w-6 h-6" />
            Tirar foto ou escolher da galeria
          </button>
        );
      case FormFieldType.CONFORMITY:
        return (
          <div className="flex gap-2">
            {[
              { value: 'conforme', label: 'Conforme' },
              { value: 'nao_conforme', label: 'Não conforme' },
              { value: 'na', label: 'N/A' },
            ].map((o) => (
              <button
                key={o.value}
                type="button"
                disabled
                className="flex-1 text-sm font-medium border rounded-lg py-2 px-2"
                style={inputStyle}
              >
                {o.label}
              </button>
            ))}
          </div>
        );
      default:
        return <input type="text" disabled placeholder={placeholder || 'Resposta do usuário'} className="h-[38px] w-full text-sm border rounded-lg px-3 py-2" style={inputStyle} />;
    }
  })();

  return (
    <div>
      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
        {field.label}
        {field.required && <span className="ml-1" style={{ color: 'var(--theme-error-color)' }}>*</span>}
      </label>
      {field.description && (
        <p className="text-xs mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>{field.description}</p>
      )}
      {control}
    </div>
  );
};

FormFieldPreview.displayName = 'FormFieldPreview';
